import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const articles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4"></i>
        <h2 className="text-xl font-semibold text-foreground-700 mb-2">分类不存在</h2>
        <Link to="/" className="text-accent-500 hover:text-accent-600 transition-colors">
          返回首页
        </Link>
      </div>
    );
  }

  const collectionPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - ${siteInfo.name}`,
    description: category.description,
    url: `${siteInfo.url}/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: articles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${siteInfo.url}/article/${article.id}`,
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - ${siteInfo.name}`}
        description={category.description}
        keywords={`${category.name},${siteInfo.keywords}`}
        canonicalUrl={`${siteInfo.url}/category/${slug}`}
        schema={collectionPageSchema}
      />

      <main>
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: category.name },
          ]}
        />

        {/* Category Header */}
        <section className="px-4 md:px-6 py-8 max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500/10">
              <i className={`${category.icon} text-accent-500 text-xl`}></i>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground-900">{category.name}</h1>
              <p className="text-sm text-foreground-500 mt-0.5">{category.description}</p>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="px-4 md:px-6 pb-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                  cat.slug === slug
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Article List */}
        <section className="px-4 md:px-6 pb-12 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-5">
            <i className="ri-article-line text-accent-500"></i>
            <h2 className="text-lg font-semibold text-foreground-900">
              共 {articles.length} 篇相关爆料
            </h2>
          </div>

          {articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {articles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-background-50 rounded-lg border border-background-200">
              <i className="ri-inbox-line text-4xl text-foreground-300 mb-3 block"></i>
              <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
            </div>
          )}
        </section>
      </main>
    </>
  );
}